import { z } from 'zod';

import { LocalDateSchema } from '../admin/attendance';
import { IsoDateTimeSchema } from '../common/datetime';
import { TimezoneSchema } from '../common/fields';
import { AttendanceMarkId, SiteId } from '../common/ids';

import { AttendanceActionSchema } from './vpp';

// ---------------------------------------------------------------------------
// `GET /attendance/marks` del realm staff — historial del trabajador
// (feature 006, `contracts/staff-api.md` §1, `data-model.md` §2.1–§2.3).
//
// Nada interno viaja a Staff: ni `flags`, ni `status`, ni `location_status`, ni coordenadas,
// `vpp_mode`, dispositivo, marcador, challenge, motivo o administrador. La traducción
// (`adjusted`, `observation`) la hace el mapper del servidor.
// ---------------------------------------------------------------------------

/** Ventana del historial en días operativos (`operative_day >= hoy − 59`). */
export const STAFF_HISTORY_WINDOW_DAYS = 60;
/** Días operativos por página si no se envía `limit`. */
export const STAFF_HISTORY_PAGE_DAYS_DEFAULT = 14;
/** Máximo de días operativos por página. */
export const STAFF_HISTORY_PAGE_DAYS_MAX = 31;

/**
 * Observación visible para el trabajador: `FLAGGED` con `MISSING_CHECKOUT` ⇒ `MISSING_CHECKOUT`;
 * cualquier otro `FLAGGED` ⇒ `OBSERVED`; `VALID` ⇒ `null`.
 */
export const StaffAttendanceMarkObservationSchema = z.enum(['MISSING_CHECKOUT', 'OBSERVED']);
export type StaffAttendanceMarkObservation = z.infer<typeof StaffAttendanceMarkObservationSchema>;

/** Una marca vigente (`VALID | FLAGGED`) vista por el propio trabajador. */
export const StaffAttendanceMarkSchema = z.object({
  /** Clave de lista; la UI no lo pinta. */
  id: AttendanceMarkId,
  type: AttendanceActionSchema,
  /** Hora oficial UTC. */
  server_timestamp: IsoDateTimeSchema,
  /** Zona denormalizada de la marca: se formatea SIEMPRE con esta (nunca la actual de la sede). */
  site_timezone: TimezoneSchema,
  site: z.object({ id: SiteId, name: z.string() }),
  /** `source = MANUAL` ⇒ "(ajustada)". */
  adjusted: z.boolean(),
  observation: StaffAttendanceMarkObservationSchema.nullable(),
});
export type StaffAttendanceMark = z.infer<typeof StaffAttendanceMarkSchema>;

/** Un día operativo con sus marcas vigentes (orden cronológico ascendente). */
export const StaffAttendanceDaySchema = z.object({
  operative_day: LocalDateSchema,
  marks: z.array(StaffAttendanceMarkSchema).min(1),
  /** Suma de los pares cuya entrada cae en este día; `null` sin pares completos. */
  worked_minutes: z.number().int().nonnegative().nullable(),
});
export type StaffAttendanceDay = z.infer<typeof StaffAttendanceDaySchema>;

/**
 * Query del historial. Los parámetros desconocidos (`worker_id`, `site_id`, `from_day`…) se
 * descartan: el trabajador solo puede ver lo suyo.
 */
export const StaffAttendanceHistoryQuerySchema = z.object({
  /** Días operativos por página (llega como string en la query). */
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(STAFF_HISTORY_PAGE_DAYS_MAX)
    .default(STAFF_HISTORY_PAGE_DAYS_DEFAULT),
  /** Opaco: el `next_cursor` de la página anterior. */
  cursor: z.string().min(1).max(64).optional(),
});
export type StaffAttendanceHistoryQuery = z.infer<typeof StaffAttendanceHistoryQuerySchema>;

/** `200` de `GET /attendance/marks`. Días del más reciente al más antiguo. */
export const StaffAttendanceHistoryResponseSchema = z.object({
  days: z.array(StaffAttendanceDaySchema).max(STAFF_HISTORY_PAGE_DAYS_MAX),
  next_cursor: z.string().nullable(),
  /** Día operativo actual en la zona de la sede principal. */
  today: LocalDateSchema,
  /** Informativo para la UI ("Últimos 60 días"). */
  window_days: z.literal(STAFF_HISTORY_WINDOW_DAYS),
  server_time: IsoDateTimeSchema,
});
export type StaffAttendanceHistoryResponse = z.infer<typeof StaffAttendanceHistoryResponseSchema>;
